import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import AIRecommendations from "../components/AIRecommendations";
import { needs, offers, recentActivity, stats } from "../lib/mockData";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { HandHeart, Heart, TrendingUp, MapPin, Clock, Users, MessageCircle, Plus } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) navigate("/login");
  }, [user, navigate]);

  if (!user) return null;

  const quickStats = [
    { label: "Your contributions", value: user.contributions, icon: Heart, color: "text-secondary" },
    { label: "People helped", value: 27, icon: HandHeart, color: "text-primary" },
    { label: "Active matches", value: 3, icon: TrendingUp, color: "text-secondary" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      {/* Welcome */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
        <div>
          <div className="text-sm text-muted-foreground">Welcome back,</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            {user.name.split(" ")[0]} 👋
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            <MapPin size={13} className="inline mr-1" /> {user.city}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => navigate("/post-need")}
            data-testid="dash-post-need"
            className="rounded-full bg-primary hover:bg-primary/90 shadow-sm hover:-translate-y-0.5 transition-all"
          >
            <Plus size={16} className="mr-1" /> Post a need
          </Button>
          <Button
            onClick={() => navigate("/offer-help")}
            data-testid="dash-offer-help"
            variant="outline"
            className="rounded-full border-border hover:bg-accent"
          >
            <Heart size={16} className="mr-1" /> Offer help
          </Button>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        {quickStats.map((s, i) => (
          <Card
            key={s.label}
            data-testid={`quick-stat-${i}`}
            className={`fade-up stagger-${i + 1} rounded-2xl p-6 border-border/60 shadow-none hover:shadow-sm transition-shadow`}
          >
            <div className={`h-10 w-10 rounded-xl bg-accent flex items-center justify-center ${s.color} mb-4`}>
              <s.icon size={18} />
            </div>
            <div className="text-3xl font-display font-bold">{s.value}</div>
            <div className="text-sm text-muted-foreground mt-1">{s.label}</div>
          </Card>
        ))}
      </div>

      {/* Main grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <AIRecommendations />

          <Card className="rounded-3xl p-6 sm:p-8 border-border/60 shadow-none">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-display text-xl font-semibold">Needs near you</h3>
              <Button variant="ghost" size="sm" className="rounded-full text-primary">See all</Button>
            </div>
            <div className="space-y-3">
              {needs.slice(0, 3).map((n, i) => (
                <div
                  key={n.id}
                  data-testid={`dash-need-${n.id}`}
                  className={`fade-up stagger-${i + 1} rounded-2xl border border-border/60 p-5 hover:border-primary/40 hover:shadow-sm transition-all`}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-1.5">
                        <Badge className="rounded-full text-[10px] px-2 py-0.5 bg-primary/10 text-primary border-0">{n.category}</Badge>
                        {n.urgency === "high" && (
                          <Badge className="rounded-full text-[10px] px-2 py-0.5 bg-destructive/10 text-destructive border-0">Urgent</Badge>
                        )}
                      </div>
                      <h4 className="font-display font-semibold">{n.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{n.description}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground mt-3">
                    <span className="flex items-center gap-1"><MapPin size={12} /> {n.location}</span>
                    <span className="flex items-center gap-1"><Clock size={12} /> {n.postedAt}</span>
                    <span className="flex items-center gap-1"><Users size={12} /> {n.responses} responded</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="rounded-3xl p-6 border-border/60 shadow-none">
            <h3 className="font-display text-lg font-semibold mb-4">Recent activity</h3>
            <div className="space-y-4">
              {recentActivity.map((a, i) => (
                <div key={a.id} data-testid={`activity-${a.id}`} className={`fade-up stagger-${i + 1} flex items-start gap-3`}>
                  <div className="h-9 w-9 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-semibold text-sm shrink-0">
                    {a.user.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">
                      <span className="font-semibold">{a.user}</span>{" "}
                      <span className="text-muted-foreground">{a.action}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">{a.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-3xl p-6 border-border/60 shadow-none bg-gradient-to-br from-secondary/15 to-primary/10">
            <div className="h-10 w-10 rounded-xl bg-secondary text-secondary-foreground flex items-center justify-center mb-4">
              <MessageCircle size={18} />
            </div>
            <h3 className="font-display font-semibold text-lg mb-1">Your Kindness Certificate</h3>
            <p className="text-sm text-muted-foreground mb-4">
              You've earned a Bronze tier certificate. Share your impact on LinkedIn, Twitter or add it to your résumé.
            </p>
            <Button
              onClick={() => navigate("/certificate")}
              size="sm"
              className="rounded-full bg-primary hover:bg-primary/90"
              data-testid="dash-view-certificate"
            >
              View & share →
            </Button>
          </Card>

          <Card className="rounded-3xl p-6 border-border/60 shadow-none bg-secondary/10">
            <div className="h-10 w-10 rounded-xl bg-secondary text-secondary-foreground flex items-center justify-center mb-4">
              <MessageCircle size={18} />
            </div>
            <h3 className="font-display font-semibold text-lg mb-1">Community pulse</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {offers.length + needs.length} new posts in your area today. Your neighborhood is buzzing with kindness.
            </p>
            <Button variant="ghost" size="sm" className="rounded-full text-secondary hover:bg-secondary/20" data-testid="community-pulse-cta">
              Explore posts →
            </Button>
          </Card>

          <Card className="rounded-3xl p-6 border-border/60 shadow-none">
            <h3 className="font-display text-lg font-semibold mb-4">Global impact</h3>
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Meals delivered</span>
                <span className="font-display font-semibold">{stats.mealsDelivered.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Countries</span>
                <span className="font-display font-semibold">{stats.countries}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total matches</span>
                <span className="font-display font-semibold">{stats.matchesMade.toLocaleString()}</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
